import { useState } from 'react';
import SEO from '../../../components/SEO';

export default function InflationAnalysisPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://buyinzagreb.com';

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Utjecaj inflacije na cijene nekretnina Zagreb",
    "description": "Stručna analiza kako ekonomski trendovi utječu na zagrebačko tržište nekretnina i zašto je sada pravo vrijeme za kupnju stanove Zagreb.",
    "image": "https://public.readdy.ai/ai/img_res/7021b1dd7bb718c4c53cf8a6b95e7874.jpg",
    "author": {
      "@type": "Organization",
      "name": "BuyInZagreb Analitički tim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BuyInZagreb",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": "2024-01-20",
    "dateModified": "2024-01-20",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/analiza-cijena-nekretnina-zagreb`
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/blog-inflation-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Utjecaj inflacije na cijene nekretnina Zagreb 2024 | Analiza tržišta"
        description="Stručna analiza kako inflacija utječe na cijene nekretnina Zagreb. Projekcije, trendovi i savjeti za investitore. Agencije za nekretnine Zagreb."
        keywords="cijene nekretnina Zagreb, inflacija, tržište nekretnina, stanovi Zagreb, investicije nekretnine, agencije za nekretnine Zagreb"
        canonical={`${siteUrl}/blog/analiza-cijena-nekretnina-zagreb`}
        schemaData={articleSchema}
      />

      {/* Header - Consistent with main page */}
      <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <a href="/">
                <img 
                  src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                  alt="BuyInZagreb Logo" 
                  className="h-10 w-auto"
                />
              </a>
            </div>
            
            <nav className="hidden lg:flex items-center space-x-6">
              {[
                { name: 'Početna', href: '/' },
                { name: 'Nekretnine', href: '/#listings' },
                { name: 'Usluge', href: '/#services' },
                { name: 'O nama', href: '/#about' },
                { name: 'Blog', href: '/#blog-tools' },
                { name: 'Kontakt', href: '/#contact' }
              ].map((item) => (
                <a 
                  key={item.name}
                  href={item.href} 
                  className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  {item.name}
                </a>
              ))}
            </nav>

            <div className="flex items-center space-x-3">
              <a 
                href="/#contact"
                className="hidden lg:flex bg-red-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Kontaktirajte nas
              </a>
              <button 
                className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <nav className="px-4 py-4 space-y-3">
              <a 
                href="/" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Početna
              </a>
              <a 
                href="/#listings" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Nekretnine
              </a>
              <a 
                href="/#services" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Usluge
              </a>
              <a 
                href="/#about" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                O nama
              </a>
              <a 
                href="/#blog-tools" 
                className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-red-50 hover:text-red-600 rounded-lg font-medium transition-colors"
              >
                Blog
              </a>
              <a 
                href="/#contact" 
                className="block w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors text-center"
              >
                Kontaktirajte nas
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-16">
        {/* Article Header */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Utjecaj inflacije na cijene nekretnina Zagreb
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Stručna analiza kako ekonomski trendovi utječu na zagrebačko tržište nekretnina i zašto je sada pravo vrijeme za kupnju stanove Zagreb.
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span><i className="ri-calendar-line mr-2"></i>20. siječnja 2024.</span>
                <span><i className="ri-time-line mr-2"></i>12 min čitanja</span>
                <span><i className="ri-user-line mr-2"></i>BuyInZagreb Analitički tim</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl mb-12">
              <img 
                src="https://readdy.ai/api/search-image?query=Real%20estate%20market%20analysis%20Zagreb%20Croatia%2C%20property%20price%20trends%2C%20inflation%20impact%2C%20economic%20charts%20and%20graphs%2C%20professional%20market%20research%20environment%2C%20financial%20data%20visualization&width=1200&height=675&seq=blog-inflation-main&orientation=landscape"
                alt="Analiza tržišta nekretnina Zagreb"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Trenutno stanje na tržištu nekretnina Zagreb</h2>
              
              <p className="text-gray-700 leading-relaxed mb-8">
                Zagrebačko tržište nekretnina prolazi kroz period značajnih promjena uzrokovanih globalnim ekonomskim trendovima i lokalnim faktorima. Cijene stanova Zagreb porasle su za 15-20% u posljednje dvije godine, što je direktna posljedica inflacijskih pritisaka, povećane potražnje i ograničene ponude kvalitetnih nekretnina.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Agencije za nekretnine Zagreb bilježe rekordnu aktivnost kupaca, posebno u segmentu novogradnje i renoviranih stanova u centru grada. Stručnjaci procjenjuju da će ovaj trend rasta cijena nekretnina Zagreb nastaviti i u 2024. godini, iako u umjerenijem tempu od prethodnih godina.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Kako inflacija utječe na vrijednost nekretnina?</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Inflacija tradicionalno pozitivno utječe na vrijednost nekretnina jer predstavljaju realnu imovinu koja zadržava vrijednost kroz vrijeme. U uvjetima kada opća razina cijena raste 8-10% godišnje, nekretnine postaju sigurno utočište za očuvanje kupovne moći kapitala.
              </p>

              <div className="bg-blue-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Ključni faktori koji utječu na cijene nekretnina:</h3>
                <ul className="space-y-3">
                  {[
                    'Rast troškova građevinski materijala (30-40% u posljednje 2 godine)',
                    'Povećanje najamnina za stanove Zagreb (20-25% rast)',
                    'Ograničena ponuda zemljišta za gradnju u centru Zagreba',
                    'Povećana potražnja domaćih i stranih kupaca',
                    'Niže kamatne stope na stambene kredite od inflacije',
                    'Demografski trendovi i urbanizacija'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-arrow-right-line text-blue-600 mr-3 mt-1"></i>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-gray-700 leading-relaxed mb-8">
                Građevinski sektor suočava se s kontinuiranim rastom troškova materijala i rada, što direktno utječe na konačne cijene nekretnina. Čelik je poskupio za 40%, cement za 25%, a troškovi rada za 30-35% u odnosu na predinflacijsko razdoblje.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Analiza cijena po kvartovima Zagreba</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Detaljna analiza tržišta nekretnina Zagreb pokazuje različite trendove ovisno o lokaciji. Centar grada i Gornji grad zadržavaju najviše cijene, dok se najbolji omjer cijene i kvalitete nalazi u kvartovima poput Trnja, Vrapča i Dubrave.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                <div className="bg-red-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-red-600 mb-4">Premium lokacije</h4>
                  <p className="text-sm text-gray-600 mb-4">Centar, Gornji grad, Donji grad</p>
                  <div className="text-2xl font-bold text-gray-900 mb-2">3.500-4.500 €/m²</div>
                  <p className="text-sm text-gray-600">Rast: +18% godišnje</p>
                  <ul className="text-xs mt-4 space-y-1">
                    <li>• Povijesne zgrade</li>
                    <li>• Luksuzni penthouse apartmani</li>
                    <li>• Kompletno renovirani stanovi</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-green-600 mb-4">Srednje zone</h4>
                  <p className="text-sm text-gray-600 mb-4">Maksimir, Novi Zagreb, Trnje</p>
                  <div className="text-2xl font-bold text-gray-900 mb-2">2.200-3.200 €/m²</div>
                  <p className="text-sm text-gray-600">Rast: +15% godišnje</p>
                  <ul className="text-xs mt-4 space-y-1">
                    <li>• Odlična prometna povezanost</li>
                    <li>• Novogradnja i adaptirana</li>
                    <li>• Obiteljski kvartovi</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-blue-600 mb-4">Prilika za investiciju</h4>
                  <p className="text-sm text-gray-600 mb-4">Dubrava, Vrapče, Savski gaj</p>
                  <div className="text-2xl font-bold text-gray-900 mb-2">1.800-2.500 €/m²</div>
                  <p className="text-sm text-gray-600">Rast: +12% godišnje</p>
                  <ul className="text-xs mt-4 space-y-1">
                    <li>• Najbolji omjer cijene/kvalitete</li>
                    <li>• Potencijal rasta vrijednosti</li>
                    <li>• Infrastrukturni projekti</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Zašto je sada pravo vrijeme za kupnju nekretnine?</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Usprkos visokim cijenama, trenutni trenutak predstavlja povoljnu priliku za kupnju nekretnine u Zagrebu iz nekoliko ključnih razloga. Kamatne stope na stambene kredite još uvijek su niže od stope inflacije, što znači da se realno zaduživanje odvija uz negativnu realnu kamatu.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Agencije za nekretnine Zagreb izvještavaju o povećanoj aktivnosti investitora koji prepoznaju nekretnine kao siguran način očuvanja vrijednosti kapitala. Dodatno, Europska unija planira značajna ulaganja u infrastrukturne projekte u Zagrebu što će dodatno povećati atraktivnost pojedinih lokacija.
              </p>

              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-green-600 mb-4">Prednosti kupnje u inflacijskom okruženju:</h3>
                <ul className="space-y-3">
                  {[
                    'Fiksna kamata na kredit ostaje ista dok inflacija "pojede" realnu vrijednost duga',
                    'Najamnine rastu s inflacijom što povećava prinos od investicije',
                    'Nekretnina se automatski revalorizira s rastom općih cijena',
                    'Zaštićenost od daljnje deprecijacije novca'
                  ].map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-shield-check-line text-green-600 mr-3 mt-1"></i>
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Projekcije za budućnost tržišta nekretnina Zagreb</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Ekonomski analitičari predviđaju umjereno usporavanje rasta cijena nekretnina Zagreb u narednih 18 mjeseci, ali i dalje pozitivne trendove. Očekuje se rast od 8-12% godišnje, što je i dalje značajno iznad europskog prosjeka koji se kreće oko 5-7%.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Ključni pokretači budućeg rasta bit će EU fondovi za obnovu nakon potresa, planovi za modernizaciju javnog prijevoza i povećanje turističke aktivnosti. Agencije za nekretnine Zagreb već sada bilježe povećan interes stranih kupaca, posebno iz regije i EU zemalja.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategije investiranja u nekretnine tijekom inflacije</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Iskusni investitori prilagođavaju svoje strategije inflacijskom okruženju fokusirajući se na lokacije s najvećim potencijalom rasta. Kvartovi u blizini planiranih infrastrukturnih projekata, kao što je modernizacija tramvajskih linija i gradnja novih trgovačkih centara, pokazuju posebno dobre perspektive.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Diverzifikacija portfelja kroz kupnju različitih tipova nekretnina - od stanova za najam do poslovnih prostora - omogućuje maksimiziranje prinosa i smanjivanje rizika. Agencije za nekretnine Zagreb pružaju stručno savjetovanje o najperspektivnijim investicijskim prilikama.
              </p>

              <div className="bg-yellow-50 rounded-2xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-yellow-600 mb-4">Ključni savjeti za kupce u inflacijskom okruženju:</h3>
                <ul className="space-y-3">
                  {[
                    'Ne čekajte "savršen trenutak" - cijene nekretnina nastavljaju rasti',
                    'Fokusirajte se na lokacije s dobrom infrastrukturom i razvojnim planovima',
                    'Koristite mogućnosti kreditnog financiranja dok su kamate još uvijek povoljne',
                    'Razmislite o nekretnini kao dugoročnoj investiciji, ne kratkoročnoj spekulaciji'
                  ].map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <i className="ri-lightbulb-line text-yellow-600 mr-3 mt-1"></i>
                      <span className="text-gray-700 font-medium">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Utjecaj vanjskih faktora na zagrebačko tržište</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Globalni ekonomski trendovi imaju direktan utjecaj na lokalnu scenu nekretnina Zagreb. Povećanje cijena energije, supply chain problemi i geopolitička nestabilnost stvaraju dodatkove inflacijske pritiske koji se reflektiraju na cijene građevinski materijala i radne snage.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                S druge strane, članstvo Hrvatske u eurozoni donosi stabilnost valute i lakši pristup europskim tržištima kapitala. To omogućuje povoljnije uvjete kreditiranja i povećava atraktivnost zagrebačkih nekretnina za strane investitore koji više ne nose valutni rizik.
              </p>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">Zaključak - Pristup tržištu s racijlnim optimizmom</h2>

              <p className="text-gray-700 leading-relaxed mb-6">
                Analiza pokazuje da unatoč visokim cijenama, nekretnine Zagreb zadržavaju svoju atraktivnost kao sigurna dugoročna investicija. Kombinacija demografskih trendova, ekonomskog rasta i infrastrukturnih ulaganja stvara solidne temelje za daljnji razvoj tržišta.
              </p>

              <p className="text-gray-700 leading-relaxed mb-8">
                Kupci koji pristupe tržištu s dugročnom perspektivom i odgovornim financijskim planiranjem mogu očekivati pozitivne povrate na investiciju. Ključ uspjeha leži u odabiru prave lokacije, kvalitetne nekretnine i rada s iskusnim agencijama za nekretnine Zagreb koje poznaju sve nijanse lokalnog tržišta.
              </p>
            </div>

            {/* CTA Section */}
            <div className="bg-gray-50 rounded-3xl p-12 text-center mt-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Želite detaljnu analizu za svoju investiciju?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                Naš analitički tim priprema personalizirane analize tržišta za kupce nekretnina Zagreb. Dobit ćete detaljne projekcije cijena, analizu lokacije i preporuke za optimalnu investiciju.
              </p>
              
              <form 
                onSubmit={handleSubmit}
                className="max-w-md mx-auto space-y-4 mb-8"
                data-readdy-form="blog-inflation-analysis"
              >
                <input 
                  type="text"
                  name="name"
                  placeholder="Ime i prezime"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <input 
                  type="email"
                  name="email"
                  placeholder="Email adresa"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
                <select 
                  name="investment_type"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors pr-8"
                  required
                >
                  <option value="">Tip investicije</option>
                  <option value="own-use">Stanovanje</option>
                  <option value="rental">Investicijski najam</option>
                  <option value="business">Poslovni prostor</option>
                  <option value="development">Razvoj nekretnine</option>
                </select>
                <textarea 
                  name="requirements"
                  placeholder="Opišite vaše zahtjeve i interesne lokacije..."
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500 transition-colors h-32 resize-none"
                  required
                ></textarea>
                
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-red-600 text-white py-4 rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Šalje se...' : 'Zatraži stručnu analizu'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-xl text-center">
                    Hvala! Analitički tim će vam se javiti u roku od 24 sata.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl text-center">
                    Greška prilikom slanja. Molimo pokušajte ponovo.
                  </div>
                )}
              </form>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-download-line mr-3"></i>
                  Preuzmi izvještaj
                </button>
                <a 
                  href="/"
                  className="bg-white/80 backdrop-blur-lg border border-gray-200 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-arrow-left-line mr-3"></i>
                  Povratak na početnu
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      {/* Footer - Simplified */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold mb-4">BuyInZagreb</h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Vodeća agencija za nekretnine u Zagrebu s više od 10 godina iskustva u prodaji stanova i kuća.
          </p>
          <div className="flex justify-center items-center gap-6">
            <a href="/#contact" className="text-gray-300 hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}